import type { Messaging } from "firebase/messaging";

import { api } from "./api";
import { readFirebaseConfig, VAPID_KEY } from "./firebase";
import { ensureServiceWorker } from "./pwa";

export type PushStatus =
  /** 브라우저가 웹 푸시를 지원하지 않음(iOS 는 홈화면에 설치해야 지원). */
  | "unsupported"
  /** `.env` 에 Firebase 웹 설정 / VAPID 키가 없음 → 푸시 비활성. */
  | "unconfigured"
  /** 아직 권한을 묻지 않음 → 사용자 조작으로 요청해야 한다. */
  | "default"
  | "granted"
  | "denied";

function browserSupportsPush(): boolean {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function pushStatus(): PushStatus {
  if (!browserSupportsPush()) return "unsupported";
  if (!readFirebaseConfig() || !VAPID_KEY) return "unconfigured";
  return Notification.permission;
}

// firebase SDK 는 실제로 필요할 때만 내려받는다(초기 로딩 비용 절감).
let messagingPromise: Promise<Messaging | null> | null = null;

async function createMessaging(): Promise<Messaging | null> {
  const config = readFirebaseConfig();
  if (!config) return null;

  const { getMessaging, isSupported } = await import("firebase/messaging");
  if (!(await isSupported())) return null;

  const { getApp, getApps, initializeApp } = await import("firebase/app");
  const app = getApps().length > 0 ? getApp() : initializeApp(config);
  return getMessaging(app);
}

function messaging(): Promise<Messaging | null> {
  messagingPromise ??= createMessaging().catch((e: unknown) => {
    console.warn("[push] Firebase 초기화 실패:", e);
    return null;
  });
  return messagingPromise;
}

/**
 * FCM 웹 토큰을 발급받아 `POST /api/devices` 로 등록한다.
 * 백엔드는 firebase-admin 으로 멀티캐스트하므로 안드로이드/웹 토큰을 같은 방식으로 처리한다.
 *
 * 권한이 `default` 인 상태에서 호출하면 브라우저 권한 창이 뜨므로 **사용자 조작**
 * (알림 켜기 버튼)에서만 호출한다. 이미 `granted` 면 앱 시작 시 조용히 재등록해도 된다.
 * 미설정/거부/미지원 환경에서는 `null` 을 반환하고 조회 기능에는 영향이 없다.
 */
export async function registerForPush(): Promise<string | null> {
  const status = pushStatus();
  if (status === "unsupported" || status === "unconfigured" || status === "denied") {
    return null;
  }

  try {
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return null;

    const instance = await messaging();
    if (!instance) return null;

    const registration = await ensureServiceWorker();
    if (!registration) return null;

    const { getToken } = await import("firebase/messaging");
    const token = await getToken(instance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return null;

    await api.registerDevice(token);
    return token;
  } catch (e) {
    // Firebase 미설정 / 푸시 서비스 차단 등 — 조용히 무시.
    console.warn("[push] 토큰 등록을 건너뜁니다:", e);
    return null;
  }
}

/**
 * 앱이 열려 있는 동안 도착한 알림을 받는다(백그라운드는 서비스워커가 처리).
 * 반환값은 구독 해제 함수.
 */
export async function onForegroundMessage(
  handler: (message: { title: string; body: string }) => void,
): Promise<() => void> {
  const instance = await messaging();
  if (!instance) return () => {};

  const { onMessage } = await import("firebase/messaging");
  return onMessage(instance, (payload) => {
    handler({
      title: payload.notification?.title ?? "Unearth",
      body: payload.notification?.body ?? "",
    });
  });
}
